import { Component, OnInit, AfterViewInit, OnDestroy } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { ToastrService } from "ngx-toastr";
import {
  DEFAULT_GUIDE_SLOT_PACKS,
  GuideSlotBalance,
  GuideSlotPack,
  GuideSlotService,
} from "src/app/Services/guide-slot.service";
import { AuthModalService } from "src/app/Services/auth-modal.service";
import { loadPayPalSdk } from "src/app/shared/utils/paypal-sdk.util";
import {
  clearCoinPurchaseReturnUrl,
  resolveCoinPurchaseReturnUrl,
  setCoinPurchaseReturnUrl,
} from "src/app/shared/utils/coin-purchase-return.util";
import { environment } from "src/environments/environment";

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const e = err as {
    error?: { error?: string; message?: string };
    message?: string;
    status?: number;
  };
  return (
    e?.error?.error ||
    e?.error?.message ||
    e?.message ||
    (e?.status ? `Request failed (${e.status}).` : fallback)
  );
}

declare var paypal: any;

@Component({
  selector: "app-purchase-guide-slots",
  templateUrl: "./purchase-guide-slots.component.html",
  styleUrls: ["./purchase-guide-slots.component.scss"],
})
export class PurchaseGuideSlotsComponent implements OnInit, AfterViewInit, OnDestroy {
  guidePacks: GuideSlotPack[] = [...DEFAULT_GUIDE_SLOT_PACKS];
  guideBalance: GuideSlotBalance | null = null;
  selectedPackId = "try";
  isLoadingBalance = false;
  purchaseInProgress = false;
  returnUrl: string | null = null;

  readonly paypalClientId =
    environment.paypalClientId ||
    "AVk8ODiI6Nh105imMWm_0NFp9mz6ofDjiA2Di3Usq2gaeZ66880LYfgBwjyGjuxnm_M1vsozkp0T2VXg";

  private paypalReady = false;
  private paypalButtonsInstance: {
    close: () => Promise<void>;
    render: (selector: string) => Promise<void>;
  } | null = null;
  private paypalInitTimer: ReturnType<typeof setTimeout> | null = null;
  private paypalInitGeneration = 0;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private guideSlotService: GuideSlotService,
    private authModalService: AuthModalService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    const userData = JSON.parse(localStorage.getItem("LoggedInUser") || "null");
    const initialReturnUrl = resolveCoinPurchaseReturnUrl(
      this.route.snapshot.queryParamMap.get("returnUrl")
    );

    if (!userData) {
      const queryParams = new URLSearchParams({
        pack: this.route.snapshot.queryParamMap.get("pack") || "try",
      });
      if (initialReturnUrl) {
        queryParams.set("returnUrl", initialReturnUrl);
        setCoinPurchaseReturnUrl(initialReturnUrl);
      }
      this.authModalService.openModal(
        "registration",
        `/purchase-guide-slots?${queryParams.toString()}`
      );
      return;
    }

    this.route.queryParamMap.subscribe((params) => {
      const packChanged = this.syncSelectedPack(params.get("pack"));
      this.returnUrl = resolveCoinPurchaseReturnUrl(params.get("returnUrl"));
      if (this.returnUrl) {
        setCoinPurchaseReturnUrl(this.returnUrl);
      }
      if (packChanged) {
        this.schedulePayPalInit();
      }
    });

    this.loadBalance();
  }

  ngAfterViewInit(): void {
    this.loadPayPalScript();
  }

  ngOnDestroy(): void {
    if (this.paypalInitTimer) {
      clearTimeout(this.paypalInitTimer);
      this.paypalInitTimer = null;
    }
    this.paypalInitGeneration += 1;
    void this.closePayPalButtonsAsync();
  }

  get selectedPack(): GuideSlotPack | undefined {
    return (
      this.guidePacks.find((p) => p.id === this.selectedPackId) ||
      this.guidePacks[0]
    );
  }

  selectPack(packId: string): void {
    if (this.selectedPackId === packId) {
      return;
    }

    this.selectedPackId = packId;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { pack: packId },
      queryParamsHandling: "merge",
      replaceUrl: true,
    });
    this.schedulePayPalInit();
  }

  goToPricing(): void {
    this.router.navigate(["/pricing"], { fragment: "guide-topups" });
  }

  private navigateBackAfterPurchase(): void {
    const target = resolveCoinPurchaseReturnUrl(this.returnUrl);
    if (!target) {
      return;
    }

    clearCoinPurchaseReturnUrl();
    setTimeout(() => {
      this.router.navigateByUrl(target);
    }, 1500);
  }

  private loadBalance(): void {
    this.isLoadingBalance = true;
    this.guideSlotService.getBalance().subscribe({
      next: (balance) => {
        this.guideBalance = balance;
        if (balance.packs?.length) {
          this.guidePacks = balance.packs;
          this.syncSelectedPack(this.selectedPackId);
        }
        this.isLoadingBalance = false;
      },
      error: () => {
        this.isLoadingBalance = false;
      },
    });
  }

  private loadPayPalScript(): void {
    loadPayPalSdk({
      clientId: this.paypalClientId,
      intent: "capture",
      currency: "GBP",
    })
      .then(() => {
        this.paypalReady = true;
        this.schedulePayPalInit();
      })
      .catch(() => {
        this.paypalReady = false;
        this.toastr.error(
          "PayPal checkout could not be loaded. Please refresh and try again.",
          "Payment unavailable"
        );
      });
  }

  private syncSelectedPack(packId?: string | null): boolean {
    const previous = this.selectedPackId;
    const requested = (packId || this.selectedPackId || "try").toLowerCase();
    if (this.guidePacks.some((pack) => pack.id === requested)) {
      this.selectedPackId = requested;
      return previous !== this.selectedPackId;
    }

    if (this.guidePacks.length > 0) {
      this.selectedPackId = this.guidePacks[0].id;
    }
    return previous !== this.selectedPackId;
  }

  private schedulePayPalInit(): void {
    if (!this.paypalReady) {
      return;
    }

    if (this.paypalInitTimer) {
      clearTimeout(this.paypalInitTimer);
    }

    this.paypalInitTimer = setTimeout(() => {
      this.paypalInitTimer = null;
      void this.initializePayPalButtons();
    }, 100);
  }

  private async closePayPalButtonsAsync(): Promise<void> {
    const instance = this.paypalButtonsInstance;
    if (!instance) {
      return;
    }

    this.paypalButtonsInstance = null;
    try {
      await instance.close();
    } catch {
      // PayPal may already be closed if the container was cleared.
    }
  }

  private async initializePayPalButtons(): Promise<void> {
    const generation = ++this.paypalInitGeneration;

    if (!this.paypalReady || typeof paypal === "undefined") {
      return;
    }

    const container = document.getElementById("paypal-guide-slot-container");
    if (!container) {
      return;
    }

    await this.closePayPalButtonsAsync();
    if (generation !== this.paypalInitGeneration) {
      return;
    }

    container.innerHTML = "";

    const pack = this.selectedPack;
    if (!pack) {
      return;
    }

    const price = Number(pack.priceGbp);
    if (!Number.isFinite(price) || price <= 0) {
      this.toastr.error(
        "This guide pack price is invalid. Please refresh and try again.",
        "Payment unavailable"
      );
      return;
    }

    const buttons = paypal.Buttons({
      style: {
        shape: "rect",
        color: "gold",
        layout: "vertical",
        label: "paypal",
      },
      createOrder: (_data: unknown, actions: any) => {
        return actions.order.create({
          purchase_units: [
            {
              description: `${pack.guides} guide slot(s) — ${pack.name}`,
              amount: {
                currency_code: "GBP",
                value: price.toFixed(2),
              },
            },
          ],
        });
      },
      onApprove: (data: { orderID?: string }, actions: any) => {
        this.purchaseInProgress = true;
        return actions.order
          .capture()
          .then((details: { id?: string }) => {
            const orderId = details?.id || data?.orderID;
            if (!orderId) {
              throw new Error("Missing PayPal order id");
            }

            this.guideSlotService.purchase(pack.id, orderId).subscribe({
              next: (balance) => {
                this.guideBalance = balance;
                this.purchaseInProgress = false;
                this.toastr.success(
                  `${pack.guides} guide slot(s) added to your account.`,
                  "Purchase complete"
                );
                this.navigateBackAfterPurchase();
              },
              error: (err) => {
                this.purchaseInProgress = false;
                this.toastr.error(
                  extractApiErrorMessage(
                    err,
                    "Payment received but guide slots could not be credited. Contact support with your PayPal receipt."
                  )
                );
              },
            });
          })
          .catch(() => {
            this.purchaseInProgress = false;
            this.toastr.error("PayPal payment could not be completed. Please try again.");
          });
      },
      onError: () => {
        this.purchaseInProgress = false;
        this.toastr.error("PayPal checkout failed. Please try again.");
      },
      onCancel: () => {
        this.purchaseInProgress = false;
      },
    });

    if (generation !== this.paypalInitGeneration) {
      await buttons.close().catch(() => undefined);
      return;
    }

    this.paypalButtonsInstance = buttons;

    try {
      await buttons.render("#paypal-guide-slot-container");
    } catch {
      if (generation === this.paypalInitGeneration) {
        this.paypalButtonsInstance = null;
        this.toastr.error(
          "PayPal button could not be displayed. Please refresh and try again.",
          "Payment unavailable"
        );
      }
    }
  }
}
